/**
 * @file BloodPressureRecordsView.jsx
 * @author Fernando Ruano
 * @description Blood Pressure Records View Page
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import {
  PlusCircleOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { groupBy } from 'lodash';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ModalOptions from '@nephro/components/ModalOptions';
import ConfirmModal from '@nephro/components/ConfirmModal';
import ModalInputsBP from '@nephro/pages/PatientRecords/Components/ModalInputsBP/modalInputsBP';

// Helpers
import {
  bloodPressureValidation,
  sortByDate
} from '@nephro/commons/helpers/utils';

// Styles
import { Empty } from 'antd';
import styles from '../../styles.module.css';

function BloodPressureRecordsView({
  bloodPressureActions,
  patientData,
  isPacient
}) {
  const [visible, setVisible] = React.useState(false);
  const [visibleConfirm, setVisibleConfirm] = React.useState(false);
  const [systolic, setSystolic] = React.useState(null);
  const [diastolic, setDiastolic] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(moment());
  const [selectedTime, setSelectedTime] = React.useState(moment());
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [visibleDrawer, setVisibleDrawer] = React.useState(false);
  const [bloodPressureList, setBloodPressureList] = React.useState({});
  const [bloodPressureDates, setBloodPressureDates] = React.useState([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const scrollRef = React.useRef(null);
  const { t: translate } = useTranslation();

  const {
    createBloodPressure,
    getBloodPressureByPatientId,
    deleteBloodPressureById,
    updateBloodPressure
  } = bloodPressureActions;

  const scrollToBottom = () => {
    // scrollRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const formatDate = (date) => moment(date).format('YYYY-MM-DD');
  const formatTime = (date) => moment(date).format('HH:mm:ss');

  // Function to get and set blood pressure list
  const onGetBloodPressureList = (_, response) => {
    if (response?.data) {
      let list = response?.data;

      list = sortByDate(list, 'example_date');

      let newBloodPressureList = list.map((itm) => {
        return {
          date: formatDate(itm.example_date),
          ...itm
        };
      });
      newBloodPressureList = groupBy(newBloodPressureList, 'date');
      const datesKeys = Object.keys(newBloodPressureList);

      setBloodPressureList(newBloodPressureList);
      setBloodPressureDates(datesKeys);
    }
  };

  // Get blood pressure list
  const getBloodPressures = () =>
    getBloodPressureByPatientId(
      { id: patientData?.id_patient },
      onGetBloodPressureList
    );

  // Create blood pressure record
  const onDeleteBloodPressure = () => {
    deleteBloodPressureById(
      { id: selectedItem.id_blood_pressure },
      getBloodPressures
    );
    setVisibleConfirm(false);
  };

  const onUpdateBloodPressure = () => {
    const exampleDate = `${formatDate(selectedDate)}T${formatTime(
      selectedTime
    )}`;
    const data = {
      id: selectedItem.id_blood_pressure,
      id_blood_pressure: selectedItem.id_blood_pressure,
      example_date: exampleDate,
      sistolica_value: `${systolic}`,
      diastolica_value: `${diastolic}`,
      patient_colombia: {
        ...selectedItem.patient_colombia
      }
    };
    setVisibleConfirm(false);
    updateBloodPressure(data, getBloodPressures);
  };

  const onSetItems = (itm) => {
    setDiastolic(parseInt(itm.diastolica_value, 10));
    setSystolic(parseInt(itm.sistolica_value, 10));
    setSelectedDate(moment(itm.example_date));
    setSelectedTime(moment(itm.example_date));
  };

  const onOpenEdit = (itm) => {
    setVisibleDrawer(false);
    setIsDeleting(false);
    onSetItems(itm);
    setIsEditing(true);
    setVisible(true);
  };

  const onOpenDelete = (itm) => {
    setVisibleDrawer(false);
    setIsEditing(false);
    onSetItems(itm);
    setIsDeleting(true);
    setVisibleConfirm(true);
  };

  const modalOptions = [
    {
      icon: <EditOutlined />,
      textOption: 'patientRecords.edit',
      type: 'primary',
      onClick: () => onOpenEdit(selectedItem)
    },
    {
      icon: <DeleteOutlined style={{ color: 'var(--color-error)' }} />,
      textOption: 'patientRecords.delete',
      type: 'error',
      onClick: () => onOpenDelete(selectedItem)
    }
  ];

  // After create blood pressure record
  const afterCreateBloodPressure = (type) => {
    if (type === 'success') {
      getBloodPressures();
    }
  };

  // Create blood pressure record
  const onCreateBloodPressure = () => {
    const exampleDate = `${formatDate(selectedDate)}T${formatTime(
      selectedTime
    )}`;
    const data = {
      example_date: exampleDate,
      sistolica_value: `${systolic}`,
      diastolica_value: `${diastolic}`,
      patient_colombia: {
        ...patientData
      }
    };
    setVisibleConfirm(false);
    createBloodPressure(data, afterCreateBloodPressure);
  };

  React.useEffect(() => {
    getBloodPressures();
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [bloodPressureList]);

  const bloodPressureStateColor = (syst, diast) =>
    bloodPressureValidation(syst, diast) === 'normal'
      ? 'text-baxterGray'
      : 'text-error';

  const openMore = (itm) => {
    setVisibleDrawer(true);
    setSelectedItem(itm);
  };

  const clearInputs = () => {
    setDiastolic(null);
    setSystolic(null);
    setSelectedDate(moment());
    setSelectedTime(moment());
  };

  const openCreateNew = () => {
    setIsEditing(false);
    clearInputs();
    setVisible(true);
  };

  const validateDateText = (date) => {
    const today = formatDate(moment());
    const yesterday = formatDate(moment().subtract(1, 'days'));
    switch (date) {
      case today:
        return translate('calendarMg.today'); // Today
      case yesterday:
        return translate('patientRecords.yesterday'); // Yesterday
      default:
        return moment(date).format('D MMMM, YYYY'); // Past days
    }
  };

  const onConfirmCreate = () => {
    setVisible(false);
    setIsDeleting(false);
    setVisibleConfirm(true);
  };

  const onSubmitConfirm = () => {
    if (isEditing) onUpdateBloodPressure(selectedItem);
    if (isDeleting) onDeleteBloodPressure(selectedItem);
    if (!isEditing && !isDeleting) onCreateBloodPressure(selectedItem);
  };

  return (
    <div className="records-menu">
      {isPacient && (
        <div className="pl-5 pr-5">
          <ButtonFilled
            key="1"
            type="cyan"
            onActionClick={openCreateNew}
            classStyles={[styles.actionButtonCreate].join(' ')}
            textButton="patientRecords.create"
            iconComponent={<PlusCircleOutlined className={styles.buttonIcon} />}
          />
          <h3 className="text-sm text-baxterGray mt-6">
            {translate('patientRecords.textContent')}
          </h3>
        </div>
      )}
      {Object.entries(bloodPressureList).length === 0 && (
        <div className="mt-40">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={translate('commons.noData')}
            style={{ color: '#858fab', fontSize: 15 }}
          />
        </div>
      )}
      {bloodPressureDates.map((itmDate, index1) => {
        return (
          <div key={index1}>
            <h2 className="mb-3 mt-7 text-lg font-bold text-baxterGray pl-5">
              {validateDateText(itmDate)}
            </h2>
            <div
              className="w-full flex overflow-x-scroll pb-5 pl-5"
              ref={scrollRef}>
              {bloodPressureList[itmDate] !== undefined &&
                sortByDate(
                  bloodPressureList[itmDate],
                  'example_date',
                  'asc'
                )?.map((itm, index2) => {
                  return (
                    <div key={index2} className={styles.recordsCard}>
                      <div className="flex flex-row justify-between items-center">
                        <h3
                          className={`mb-0 text-sm font-bold ${bloodPressureStateColor(
                            itm.sistolica_value,
                            itm.diastolica_value
                          )}`}>
                          {itm.sistolica_value}/{itm.diastolica_value}
                        </h3>
                        {isPacient && (
                          <MoreOutlined
                            onClick={() => openMore(itm)}
                            style={{
                              fontSize: 24,
                              color: 'var(--color-baxter-gray)'
                            }}
                          />
                        )}
                      </div>
                      <h3 className="mb-0 text-sm text-baxterGray">
                        {moment(itm.example_date).format('h:mm a')}
                      </h3>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
      <ConfirmModal
        visibleConfirm={visibleConfirm}
        onCancel={() => setVisibleConfirm(false)}
        mode={isDeleting ? 'delete' : 'confirm'}
        onSubmit={onSubmitConfirm}
        onSubmitText={
          isDeleting ? 'patientRecords.delete' : 'patientRecords.confirm'
        }
        contentComponent={
          <div className="bg-highLight flex flex-col justify-center items-center p-2 rounded-md mt-2">
            <h3 className="text-base text-secondary mb-0">
              {moment(selectedDate).format('D MMMM, YYYY')} {' - '}
              {moment(selectedTime).format('h:mm a')}
            </h3>
            <h3 className="text-base text-secondary mb-0 mt-2">
              {systolic}/{diastolic}
            </h3>
          </div>
        }
      />
      <ModalInputsBP
        isEditing={isEditing}
        visible={visible}
        setVisible={setVisible}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        systolic={systolic}
        setSystolic={setSystolic}
        diastolic={diastolic}
        setDiastolic={setDiastolic}
        onConfirmCreate={onConfirmCreate}
      />
      <ModalOptions
        modalOptions={modalOptions}
        visible={visibleDrawer}
        setVisible={setVisibleDrawer}
      />
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
BloodPressureRecordsView.propTypes = {
  bloodPressureActions: PropTypes.shape({
    getBloodPressureByPatientId: PropTypes.func.isRequired,
    createBloodPressure: PropTypes.func.isRequired,
    getBloodPressureById: PropTypes.func.isRequired,
    updateBloodPressure: PropTypes.func.isRequired,
    deleteBloodPressureById: PropTypes.func.isRequired
  }).isRequired,
  patientData: PropTypes.object,
  isPacient: PropTypes.bool
};

export default BloodPressureRecordsView;
